<template>
  <v-card class="border border-gray-200 px-4 py-4" elevation="0">
    <LlTextarea
      id="conflict-query"
      :model-value="modelValue"
      label="Names to check"
      :rows="4"
      placeholder="Sample Trust Company&#10;John Doe"
      hide-details
      data-cy="conflict-query-input"
      @update:model-value="emit('update:modelValue', $event)"
      @keydown="onKeydown"
    />

    <!-- The delimiter hint is always visible, not a tooltip. It is the whole
         mitigation for a comma no longer separating names. -->
    <p class="mt-2 text-xs leading-relaxed text-gray-700">
      Separate names with a <span class="font-medium">semicolon</span> or a
      <span class="font-medium">new line</span>. A comma stays inside a name —
      <span class="italic text-gray-500">Big Corporation, Inc.</span> is
      one name.
    </p>

    <div class="mt-4 border-t border-gray-200 pt-3.5">
      <span class="text-xs font-medium uppercase tracking-wider text-gray-500">
        Checking {{ partyCount }} {{ partyCount === 1 ? 'name' : 'names' }}
      </span>
      <div v-if="parties.length" class="mt-2 flex flex-wrap gap-1.5">
        <!-- ll-standards-ignore LL091 -- the name is the identity, and two parties
             can legitimately be the same text; the index disambiguates them -->
        <v-chip
          v-for="(party, index) in parties"
          :key="`${party}-${index}`"
          size="small"
          label
          variant="outlined"
          closable
          :data-cy="`conflict-party-chip-${index}`"
          :close-label="`Remove ${party}`"
          @click:close="emit('removeParty', index)"
        >
          {{ party }}
        </v-chip>
      </div>
      <p v-else class="mt-2 text-xs text-gray-400">
        No names yet. Type one to see it appear here.
      </p>

      <div v-if="skippedParties.length" class="mt-2 text-xs text-amber-700">
        {{ skippedParties.length }}
        {{ skippedParties.length === 1 ? 'name was' : 'names were' }} skipped —
        only a role or entity word to search on.
      </div>
      <div v-if="ignoredRoleWords.length" class="mt-2 text-xs text-gray-400">
        Ignored as role words: {{ ignoredRoleWords.join(', ') }}
      </div>
    </div>

    <LlCheckbox
      class="mt-3"
      :model-value="includeTimeEntries"
      label="Include time entry descriptions — slower on large firms"
      data-cy="conflict-include-time-entries"
      @update:model-value="emit('update:includeTimeEntries', $event)"
    />

    <p class="mt-2 max-w-[76ch] text-xs leading-relaxed text-gray-500">
      Searches {{ CONFLICT_SCOPE_SENTENCE
      }}<template v-if="includeTimeEntries"
        >,
        <span class="font-medium text-gray-700"
          >and time entry descriptions</span
        >.
      </template>
      <template v-else>
        .
        <span class="font-medium text-gray-700"
          >Time entries are not included.</span
        >
      </template>
      Invoice narratives are not searched.
    </p>

    <div class="mt-4 flex flex-wrap items-center gap-3">
      <LlSecondaryButton
        color="primary"
        :disabled="!partyCount"
        :loading="running"
        data-cy="conflict-run"
        @click="emit('run')"
      >
        Run conflict check
      </LlSecondaryButton>
      <span class="text-xs text-gray-500">or press ⌘↵</span>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import LlTextarea from '@/components/LlTextarea.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { CONFLICT_SCOPE_SENTENCE } from '../constants/conflictFieldConstants';
import type { ConflictParty } from '../models/conflictTypes';

defineProps<{
  modelValue: string;
  includeTimeEntries: boolean;
  parties: string[];
  partyCount: number;
  running: boolean;
  /** From the last run — the scan decides what was unsearchable, not the chips. */
  skippedParties: ConflictParty[];
  ignoredRoleWords: string[];
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'update:includeTimeEntries': [value: boolean];
  removeParty: [index: number];
  run: [];
}>();

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
    event.preventDefault();
    emit('run');
  }
}
</script>
